/**
 * Project view — DESIGN.md §7.1–§7.4, §7.6, §7.7, §13.1, §13.3.
 *
 * "/projects/:key → default view; ?view=<id> switches; tabs: List | Board |
 * Calendar + saved views".
 *
 * One ViewConfig drives all three renderings (§7.1). The config lives in
 * component state seeded from the selected saved view, which is what makes
 * §7.6's "local until saved" true: adjusting a shared view changes nothing for
 * anyone else until Save is pressed.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { toast } from 'sonner';
import { Archive, Lock, LockOpen, MessageSquarePlus, Plus, Sparkles, Star } from 'lucide-react';
import {
  VIEW_TYPES,
  defaultViewConfig,
  effectiveSections,
  generateKeyBetween,
  sectionStateKey,
  type BulkAction,
  type ProjectHealth,
  type SectionsConfig,
  type TaskSummary,
  type ViewConfig,
  type ViewType,
} from '@slate/shared';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { LoadingRegion } from '@/components/ui/spinner';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CardPanel } from '@/components/cards/card-panel';
import { CardAside, usePeekPresence } from '@/components/cards/card-aside';
import { QuickAdd } from '@/components/cards/quick-add';
import { TaskList } from '@/components/cards/task-list';
import { TableView } from '@/components/cards/table-view';
import { ColumnsMenu } from '@/components/cards/columns-menu';
import { SortMenu } from '@/components/cards/sort-menu';
import { Combobox } from '@/components/ui/combobox';
import { SectionsMenu } from '@/components/cards/sections-menu';
import type { SectionGroup, SectionGrouping } from '@/components/cards/section-row';
import { KanbanBoard, buildColumns } from '@/components/cards/kanban-board';
import { CalendarView } from '@/components/cards/calendar-view';
import { BulkToolbar } from '@/components/cards/bulk-toolbar';
import { ViewBar } from '@/components/cards/view-bar';
import {
  useFollowProject,
  usePostStatusUpdate,
  useProject,
  useSectionMutations,
  useStatusUpdates,
} from '@/features/projects/api';
import { useSectionCollapse, useUiState } from '@/features/ui-state/api';
import {
  listRows,
  useReorderTaskInProject,
  useSetAnyCustomField,
  useTaskByNumber,
  useTaskList,
  useUpdateAnyTask,
} from '@/features/tasks/api';
import { useLookups, useUserRefs } from '@/features/lookups/api';
import {
  hasSeenDisclosure,
  recordDisclosure,
  useAiStatus,
  useDraftStatus,
} from '@/features/ai/api';
import {
  exportViewCsv,
  useBulkAction,
  useSetProjectDefaultView,
  useViewMutations,
  useViews,
} from '@/features/views/api';
import { useCurrentUser } from '@/lib/auth';
import { PREF_KEYS, usePersistedState } from '@/lib/prefs';
import { emitUi } from '@/lib/ui-events';
import { ProjectAccessDialog } from '@/components/projects/project-access-dialog';
import {
  ProjectFieldsButton,
  ProjectFieldsDialog,
} from '@/components/projects/project-fields-dialog';
import { useSubscribe } from '@/lib/realtime';
import { cn, relativeTime } from '@/lib/utils';
import { HEALTH } from './index';
import { diffTweaks, hasTweaks, readTweaks, writeTweaks } from '@/features/views/view-tweaks';

const searchSchema = z.object({
  /** The card open in the side peek, by `#number` — §13.1. */
  card: z.coerce.number().int().positive().optional(),
  /** #23 — open that peek with the caret already in the description. */
  focus: z.literal('description').optional(),
  /** The saved view in use — §13.1's `?view=<id>`. */
  view: z.string().uuid().optional(),
  type: z.enum(VIEW_TYPES).optional(),
  completed: z.coerce.boolean().optional(),
});

export const Route = createFileRoute('/projects/$key')({
  validateSearch: searchSchema,
  component: ProjectPage,
});

function ProjectPage(): JSX.Element {
  const { key } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const me = useCurrentUser();

  const { data: project, isLoading, isError } = useProject(key);
  const { data: lookups } = useLookups();
  const { data: people = [] } = useUserRefs();
  const { data: views = [] } = useViews(project?.id ?? null);

  const [postingStatus, setPostingStatus] = useState(false);
  const [managingAccess, setManagingAccess] = useState(false);
  // #27
  const [configuringFields, setConfiguringFields] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  useSubscribe({ projectIds: project ? [project.id] : [] });

  // §7.6 — the view a project opens on, unless the URL says otherwise.
  const activeViewId = search.view ?? project?.defaultViewId ?? null;
  const activeView = views.find((view) => view.id === activeViewId) ?? null;

  /**
   * The live config. Seeded from the saved view (or the project default) and
   * edited locally — §7.6's "local until saved".
   *
   * VIEW-2 — a cross-project view opened here keeps its own filters and gains
   * this project: "that view, in this project", which is what a person asking
   * for "my overdue cards" on a project page means. Its stored scope is put
   * back before saving (`configToSave`) so pointing it at a project does not
   * quietly re-scope it for everyone.
   */
  const savedConfig = useMemo<ViewConfig>(
    () =>
      activeView
        ? { ...activeView.config, projectId: project?.id ?? null }
        : defaultViewConfig({
            projectId: project?.id ?? null,
            sort: [{ field: 'manual', dir: 'asc' }],
          }),
    [activeView, project?.id],
  );
  /*
   * #75 — the reader's own tweaks, remembered.
   *
   * §7.6 keeps a view change local until saved, which is right. What was wrong
   * is that "local" meant local to the tab: re-sort a list, reload, and it is
   * gone. So the config is seeded from the saved view *plus* whatever this
   * person last changed about it, and every change is written back.
   *
   * Only the deltas are stored, so a later edit to a shared view still reaches
   * everyone who has not personally overridden that particular field.
   */
  const [config, setConfig] = useState<ViewConfig>(() => ({
    ...savedConfig,
    ...readTweaks(key, activeViewId),
  }));
  useEffect(() => {
    setConfig({ ...savedConfig, ...readTweaks(key, activeViewId) });
  }, [savedConfig, key, activeViewId]);

  /**
   * VIEW-11 — the view type is remembered per project. It lived in the URL
   * alone, so walking to another screen and back landed on the list again,
   * every time, for someone whose project *is* the board.
   */
  const [rememberedType, setRememberedType] = usePersistedState<ViewType>(
    `${PREF_KEYS.viewTypeByProject}.${key}`,
    'list',
    parseViewType,
  );
  const type: ViewType = search.type ?? activeView?.type ?? rememberedType;
  /*
   * #79 — this memory holds what the reader chose for the *default* view, and
   * nothing else.
   *
   * It used to record whatever type was on screen, including the type a saved
   * view carries as its own property. So: default view (list) → "dev board"
   * (a saved kanban view, which wrote `kanban` in here) → back to "Default
   * view", where there is no saved type to fall back on, so it read the
   * memory and stayed a board. Luke's aside is the proof — going via a saved
   * *list* view on the way back fixed it, because that view overwrote the
   * memory with `list` on its way through.
   *
   * A saved view's type is that view's business. `onSelectType` still records
   * a deliberate click, which is the gesture VIEW-11 exists to remember.
   */
  useEffect(() => {
    if (activeView !== null) return;
    if (type !== rememberedType) setRememberedType(type);
  }, [activeView, type, rememberedType, setRememberedType]);

  const showCompleted = search.completed ?? config.showCompleted;
  const effectiveConfig = useMemo<ViewConfig>(
    () => ({ ...config, showCompleted }),
    [config, showCompleted],
  );

  /*
   * #75 — write the tweaks back. Keyed off `effectiveConfig` rather than
   * `config` so `showCompleted`, which arrives through the URL, is remembered
   * with the rest of them.
   */
  const tweaks = useMemo(
    () => diffTweaks(effectiveConfig, savedConfig),
    [effectiveConfig, savedConfig],
  );
  useEffect(() => {
    writeTweaks(key, activeViewId, tweaks);
  }, [key, activeViewId, tweaks]);

  /** Back to the view as saved, forgetting what this reader had bent. */
  const resetTweaks = useCallback(() => {
    writeTweaks(key, activeViewId, {});
    setConfig(savedConfig);
  }, [key, activeViewId, savedConfig]);

  /**
   * VIEW-12 — "Unsaved" means "this saved view is not what you are looking
   * at". With no view active there is nothing to be unsaved *against*, and the
   * badge was permanent for anyone who preferred the board.
   */
  const isDirty =
    activeView !== null &&
    (JSON.stringify(effectiveConfig) !== JSON.stringify(savedConfig) || type !== activeView.type);

  /** What Save writes: the on-screen config, with the view's own scope back. */
  const configToSave = useMemo<ViewConfig>(
    () =>
      activeView
        ? { ...effectiveConfig, projectId: activeView.config.projectId ?? null }
        : effectiveConfig,
    [activeView, effectiveConfig],
  );

  /*
   * #80 — the table is the same query as the list, so it is fetched for both.
   * `rows` below is what either one renders; nothing about the request changes
   * with the layout, which is what made a fourth view type cheap.
   */
  const isRowView = type === 'list' || type === 'table';
  const listQuery = useTaskList(effectiveConfig, { enabled: Boolean(project) && isRowView });
  const { items, total } = listRows(listQuery.data);

  /* ---------------------------------------------------------------- sections */

  /**
   * §6.1 — sections, as this view asks for them. `null` on a cross-project
   * view, where a section belongs to nobody.
   */
  const sectionsConfig = effectiveSections(effectiveConfig);
  const uiState = useUiState();
  const collapse = useSectionCollapse();
  const sectionMutations = useSectionMutations(project?.id ?? '');
  const [deleting, setDeleting] = useState<SectionGroup | null>(null);

  /**
   * Collapses made *in this session* on top of whatever the view starts from.
   * A view that dictates the start state ('all', 'none', a list) still lets you
   * open a section to look inside — it just does not write that down as your
   * remembered state, because the view is the one making the claim.
   */
  const [localCollapsed, setLocalCollapsed] = useState<Record<string, boolean>>({});
  const collapsedSpec = JSON.stringify(sectionsConfig?.collapsed ?? null);
  useEffect(() => setLocalCollapsed({}), [collapsedSpec, activeViewId]);

  const remembersCollapse = sectionsConfig?.collapsed === 'remember';
  const rememberedCollapsed = uiState.data?.collapsedSections ?? [];

  const sectionGroups = useMemo<SectionGroup[]>(() => {
    if (!project || sectionsConfig?.mode !== 'grouped') return [];
    const excluded = new Set(sectionsConfig.exclude);
    const spec = sectionsConfig.collapsed;
    const startsCollapsed = (id: string | null, key: string): boolean => {
      if (spec === 'all') return true;
      if (spec === 'none') return false;
      if (Array.isArray(spec)) return spec.includes(id ?? 'none');
      return rememberedCollapsed.includes(key);
    };

    const entries: { id: string | null; name: string }[] = project.sections
      .filter((section) => !excluded.has(section.id))
      .map((section) => ({ id: section.id, name: section.name }));
    // The implicit bucket goes last — §6.1's "(No section)".
    if (!excluded.has('none')) entries.push({ id: null, name: '(No section)' });

    return entries.map((entry) => {
      const key = sectionStateKey(project.id, entry.id);
      return {
        ...entry,
        key,
        collapsed: localCollapsed[key] ?? startsCollapsed(entry.id, key),
      };
    });
  }, [project, sectionsConfig, rememberedCollapsed, localCollapsed]);

  /** Collapse or expand, remembering it only when the view said to (§6.1). */
  const setCollapsed = useCallback(
    (keys: string[], collapsed: boolean): void => {
      if (keys.length === 0) return;
      setLocalCollapsed((current) => {
        const next = { ...current };
        for (const key of keys) next[key] = collapsed;
        return next;
      });
      if (!remembersCollapse) return;
      collapse.mutate(
        collapsed
          ? { collapseSections: keys, expandSections: [] }
          : { collapseSections: [], expandSections: keys },
      );
    },
    [collapse, remembersCollapse],
  );

  /** Write a `sections` block into the live config — Save persists it (§7.6). */
  const setSectionsConfig = useCallback(
    (next: SectionsConfig): void => setConfig((current) => ({ ...current, sections: next })),
    [],
  );

  /** §6.3.1 — one row per move, wherever it lands. */
  const moveSection = (group: SectionGroup, direction: -1 | 1): void => {
    const all = project?.sections ?? [];
    const index = all.findIndex((section) => section.id === group.id);
    if (index < 0) return;
    const target = index + direction;
    if (target < 0 || target >= all.length) return;
    const position =
      direction === -1
        ? generateKeyBetween(all[target - 1]?.position ?? null, all[target]!.position)
        : generateKeyBetween(all[target]!.position, all[target + 1]?.position ?? null);
    sectionMutations.move.mutate({ id: group.id!, position });
  };

  /**
   * §6.3.1, #6 — a drag inside the list writes exactly one row.
   *
   * The list hands over the two cards the drop landed between; the position
   * comes from *their* positions in this project, not from their screen order,
   * which is what keeps two people dragging at once from corrupting each
   * other's order.
   */
  const reorderCard = (move: {
    task: TaskSummary;
    sectionId: string | null | undefined;
    prev: TaskSummary | null;
    next: TaskSummary | null;
  }): void => {
    if (!project) return;
    const positionIn = (task: TaskSummary | null): string | null =>
      task?.projects.find((entry) => entry.project.id === project.id)?.position ?? null;
    const membership = move.task.projects.find((entry) => entry.project.id === project.id);
    if (!membership) return;

    let position: string;
    try {
      position = generateKeyBetween(positionIn(move.prev), positionIn(move.next));
    } catch {
      // Two neighbours whose positions do not straddle the gap — stale data on
      // this client. Refetching is a better answer than writing a bad key.
      toast.error('That list has moved on. Refreshing it — try the drag again.');
      void listQuery.refetch();
      return;
    }

    reorder.mutate({
      task: move.task,
      projectId: project.id,
      // `undefined` from an ungrouped list means "leave the section alone".
      sectionId: move.sectionId === undefined ? membership.sectionId : move.sectionId,
      position,
    });
  };

  const grouping: SectionGrouping | undefined =
    // #80 — the table groups by section too, so this is no longer list-only.
    project && isRowView && sectionsConfig?.mode === 'grouped'
      ? {
          projectId: project.id,
          groups: sectionGroups,
          showCompleted,
          // §9.2 — every active member may reshape a project they can see (D5).
          canManage: true,
          onToggle: (group) => setCollapsed([group.key], !group.collapsed),
          onRename: (group, name) => sectionMutations.rename.mutate({ id: group.id!, name }),
          onMove: moveSection,
          onDelete: (group) => setDeleting(group),
          onCreate: (name) => sectionMutations.create.mutate({ name }),
        }
      : undefined;

  const bulk = useBulkAction();
  const viewMutations = useViewMutations();
  const setProjectDefault = useSetProjectDefaultView();
  const reorder = useReorderTaskInProject();
  const updateAny = useUpdateAnyTask();
  const setCustomField = useSetAnyCustomField();

  const openCard = useCallback(
    (number: number | undefined): void => {
      // #23's `focus` belongs to the card just created, so it is dropped
      // rather than carried onto the next card or a reopened peek.
      void navigate({
        search: (previous) => ({ ...previous, card: number, focus: undefined }),
      });
    },
    [navigate],
  );

  const setSearch = useCallback(
    (patch: Partial<z.infer<typeof searchSchema>>): void => {
      void navigate({ search: (previous) => ({ ...previous, ...patch }) });
    },
    [navigate],
  );

  const runBulk = (action: BulkAction, options?: { force?: boolean }): void => {
    bulk.run.mutate(
      { taskIds: selected, action, force: options?.force },
      { onSuccess: () => setSelected([]) },
    );
  };

  /**
   * §7.3 — "drag between columns mutates the underlying field (status change =
   * real status change with full audit + notifications)". Which field depends
   * on what the columns are made of, so the move is expressed as a bulk action
   * on one card: same service layer, same audit, and an undo in the toast for
   * free.
   */
  const moveCard = (task: TaskSummary, columnId: string): void => {
    const action: BulkAction | null =
      kanbanSource === 'status'
        ? { type: 'set_status', statusId: columnId }
        : kanbanSource === 'section'
          ? {
              type: 'move_to_section',
              projectId: project!.id,
              sectionId: columnId === 'none' ? null : columnId,
            }
          : null;

    if (action) {
      bulk.run.mutate({ taskIds: [task.id], action, force: false });
      return;
    }

    // A custom-field column: set the field to the column's option, or clear it
    // for the "(Not set)" column.
    const fieldId = kanbanSource.slice('customField:'.length);
    setCustomField.mutate({
      taskId: task.id,
      fieldId,
      value: columnId === 'none' ? null : columnId,
    });
  };

  /** §7.4 — "drag between days rewrites the date (audited)". */
  const reschedule = (task: TaskSummary, date: string | null): void => {
    const field = effectiveConfig.calendar?.dateField ?? 'dueDate';
    updateAny.mutate(
      field === 'dueDate'
        ? { taskId: task.id, dueDate: date }
        : { taskId: task.id, startDate: date },
    );
  };

  /** #44 — the peek's number outlives its URL by the length of the slide out. */
  const peek = usePeekPresence(search.card);

  if (isLoading) return <LoadingRegion label="Loading project" />;

  if (isError || !project) {
    return (
      <div className="mx-auto w-full max-w-3xl p-6">
        <EmptyState
          title={`No project with the key ${key}`}
          description="It may have been deleted, or the link may be wrong."
          action={
            <Button variant="secondary" onClick={() => void navigate({ to: '/projects' })}>
              All projects
            </Button>
          }
        />
      </div>
    );
  }

  const kanbanSource = effectiveConfig.kanban?.columnSource ?? 'status';

  /**
   * The peek's "previous / next card" — the order of the list on screen, which
   * is what someone triaging means by "the next one".
   */
  const numbers = items.map((task) => task.number);
  const cursor = search.card === undefined ? -1 : numbers.indexOf(search.card);
  const neighbours = {
    prev: cursor > 0 ? (numbers[cursor - 1] ?? null) : null,
    next: cursor >= 0 ? (numbers[cursor + 1] ?? null) : null,
  };

  const newCard = (): void =>
    emitUi('new-card', { projectId: project.id, projectKey: project.key });

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] lg:h-dvh">
      <div className="flex min-w-0 flex-1 flex-col">
        <ProjectHeader
          project={project}
          onNewCard={newCard}
          onPostStatus={() => setPostingStatus(true)}
          onManageAccess={() => setManagingAccess(true)}
          onConfigureFields={() => setConfiguringFields(true)}
        />

        <ViewBar
          views={views}
          activeViewId={activeViewId}
          type={type}
          isDirty={isDirty}
          canManageProject
          compact={<CompactProjectHeader project={project} onNewCard={newCard} />}
          onSelectView={(id) => setSearch({ view: id ?? undefined, type: undefined })}
          onSelectType={(next) => {
            setSearch({ type: next });
            setRememberedType(next);
            if (next === 'kanban' && !config.kanban) {
              setConfig((current) => ({
                ...current,
                kanban: { columnSource: 'status', hideEmpty: false },
              }));
            }
            if (next === 'calendar' && !config.calendar) {
              setConfig((current) => ({ ...current, calendar: { dateField: 'dueDate' } }));
            }
          }}
          onReset={hasTweaks(tweaks) ? resetTweaks : undefined}
          onSaveExisting={() =>
            activeView &&
            viewMutations.update.mutate({ id: activeView.id, config: configToSave, type })
          }
          onSaveAsNew={(name, shared) =>
            viewMutations.create.mutate(
              {
                name,
                type,
                scope: shared ? 'shared' : 'personal',
                projectId: project.id,
                config: effectiveConfig,
              },
              { onSuccess: (view) => setSearch({ view: view.id }) },
            )
          }
          onPublish={(shared) =>
            activeView && viewMutations.share.mutate({ id: activeView.id, shared })
          }
          onDuplicate={() =>
            activeView &&
            viewMutations.duplicate.mutate(activeView.id, {
              onSuccess: (view) => setSearch({ view: view.id }),
            })
          }
          onDelete={() =>
            activeView &&
            viewMutations.remove.mutate(activeView.id, {
              onSuccess: () => setSearch({ view: undefined }),
            })
          }
          onSetProjectDefault={() => {
            // VIEW-18 — a real mutation: it says whether it worked, and the
            // star moves without a reload.
            if (activeView) {
              setProjectDefault.mutate({ projectId: project.id, viewId: activeView.id });
            }
          }}
          onExport={() => void exportViewCsv(effectiveConfig)}
        >
          {/* R18 — "Show Completed" is per-view and lives in the URL here. */}
          <label className="text-muted-foreground flex cursor-pointer items-center gap-1.5 text-xs [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:text-sm">
            <input
              type="checkbox"
              className="[@media(pointer:coarse)]:size-5"
              checked={showCompleted}
              onChange={(event) => setSearch({ completed: event.target.checked || undefined })}
            />
            Show completed
          </label>

          {/* §6.1 — grouped or flat, what is hidden, and what opens open. */}
          {isRowView && sectionsConfig ? (
            <SectionsMenu
              config={sectionsConfig}
              sections={project.sections}
              onChange={setSectionsConfig}
              onCollapseAll={() =>
                setCollapsed(
                  sectionGroups.map((group) => group.key),
                  true,
                )
              }
              onExpandAll={() =>
                setCollapsed(
                  sectionGroups.map((group) => group.key),
                  false,
                )
              }
            />
          ) : null}

          {/* #45 — the order cards come in, for the fields with no column on
              screen; a column header is the shortcut for the ones that have. */}
          {isRowView ? (
            <SortMenu
              sort={effectiveConfig.sort}
              fields={(lookups?.fields ?? []).filter((field) => !field.archivedAt)}
              onChange={(next) => setConfig((current) => ({ ...current, sort: next }))}
            />
          ) : null}

          {isRowView ? (
            /*
             * #78 — Ben: "Change the columns section on the view bar to say
             * 'Tags' instead since they are not columns."
             *
             * He is right that they are not columns *in the list*, which is
             * Brad's #50 ruling: a list row is a set of fields, and one a card
             * has nothing to say for is simply absent. But they are not tags
             * either — a tag is a first-class thing in Slate with its own
             * colours and filters, and `tags` is deliberately not even in this
             * menu because it always renders beside the title. Calling Status,
             * Due date and Estimate "Tags" would collide with the word that
             * already means something else.
             *
             * So it says what it is, per surface: **Row fields** in the list,
             * and **Columns** in the table (#80), where they genuinely are
             * columns with headers over them. Same setting, same
             * `ViewConfig.columns`, named for what it does where you stand.
             *
             * Not plain "Fields": #27 put a *Fields* button on this same
             * header for which fields a project asks for on a card, and two
             * controls two inches apart with one word between them and two
             * different meanings is worse than the wrong word. The E2E spec
             * found that collision, which is the argument for having one.
             */
            <ColumnsMenu
              label={type === 'table' ? 'Columns' : 'Row fields'}
              columns={effectiveConfig.columns}
              fields={(lookups?.fields ?? []).filter((field) => !field.archivedAt)}
              onChange={(columns) => setConfig((current) => ({ ...current, columns }))}
            />
          ) : null}

          {type === 'kanban' ? (
            <Combobox
              className="h-8 w-44 text-xs"
              label="Column source"
              value={kanbanSource}
              options={[
                { value: 'status', label: 'Columns: status' },
                { value: 'section', label: 'Columns: section' },
                ...(lookups?.fields ?? [])
                  .filter((field) => field.type === 'select' && !field.archivedAt)
                  .map((field) => ({
                    value: `customField:${field.id}`,
                    label: `Columns: ${field.name}`,
                    keywords: [field.name],
                  })),
              ]}
              onChange={(value) =>
                setConfig((current) => ({
                  ...current,
                  kanban: { columnSource: value, hideEmpty: false },
                }))
              }
            />
          ) : null}

          {type === 'calendar' ? (
            <Combobox
              className="h-8 w-36 text-xs"
              label="Calendar date field"
              value={effectiveConfig.calendar?.dateField ?? 'dueDate'}
              options={[
                { value: 'dueDate', label: 'By due date' },
                { value: 'startDate', label: 'By start date' },
              ]}
              onChange={(value) =>
                setConfig((current) => ({
                  ...current,
                  calendar: { dateField: value as 'dueDate' | 'startDate' },
                }))
              }
            />
          ) : null}
        </ViewBar>

        {type === 'list' ? (
          <div className="flex min-h-0 flex-1 flex-col">
            {/* VIEW-6 — one line at the top of the list adds a card without a
                modal, and stays focused for the next one. */}
            <QuickAdd
              className="border-b px-3 py-1"
              projectId={project.id}
              label={`Add a card to ${project.name}`}
            />
            <TaskList
              items={items}
              total={total}
              selectedId={items.find((task) => task.number === search.card)?.id ?? null}
              dense={Boolean(search.card)}
              columns={effectiveConfig.columns}
              /* #50 — so a custom field's hover names the field. */
              fieldNames={Object.fromEntries(
                (lookups?.fields ?? []).map((field) => [field.id, field.name]),
              )}
              isLoading={listQuery.isLoading}
              isFetchingNextPage={listQuery.isFetchingNextPage}
              hasNextPage={listQuery.hasNextPage}
              fetchNextPage={() => void listQuery.fetchNextPage()}
              onOpen={(task) => openCard(task.number)}
              /*
                #6 — offered only when the order on screen is the one someone
                arranged by hand. Under a due-date sort a drag would appear to
                work and then snap back on the next refetch, which is worse
                than not offering it.
              */
              onReorder={effectiveConfig.sort[0]?.field === 'manual' ? reorderCard : undefined}
              selectedIds={selected}
              onSelectionChange={setSelected}
              sections={grouping}
              emptyTitle={`Nothing in ${project.name} yet`}
              emptyDescription="Cards you create here land in this project. A card can also live in several projects at once."
              emptyAction={<Button onClick={newCard}>Create the first card</Button>}
            />
          </div>
        ) : type === 'table' ? (
          /*
           * #80 — the same rows and the same `columns`, laid out to be read
           * down instead of across, and grouped into sections when the project
           * is. The section header spans the table, so it separates the runs
           * without moving a column inside one.
           */
          <div className="flex min-h-0 flex-1 flex-col">
            <QuickAdd
              className="border-b px-3 py-1"
              projectId={project.id}
              label={`Add a card to ${project.name}`}
            />
            <TableView
              items={items}
              total={total}
              columns={effectiveConfig.columns}
              fields={(lookups?.fields ?? []).filter((field) => !field.archivedAt)}
              selectedId={items.find((task) => task.number === search.card)?.id ?? null}
              isLoading={listQuery.isLoading}
              isFetchingNextPage={listQuery.isFetchingNextPage}
              hasNextPage={listQuery.hasNextPage}
              fetchNextPage={() => void listQuery.fetchNextPage()}
              onOpen={(task) => openCard(task.number)}
              selectedIds={selected}
              onSelectionChange={setSelected}
              sections={grouping}
              emptyTitle={`Nothing in ${project.name} yet`}
              emptyDescription="Cards you create here land in this project. A card can also live in several projects at once."
              emptyAction={<Button onClick={newCard}>Create the first card</Button>}
            />
          </div>
        ) : type === 'kanban' ? (
          <KanbanBoard
            config={effectiveConfig}
            columnSource={kanbanSource}
            columns={buildColumns(kanbanSource, lookups, project.sections)}
            selectedNumber={search.card}
            projectId={project.id}
            onOpen={(task) => openCard(task.number)}
            onMove={moveCard}
          />
        ) : (
          <CalendarView
            config={effectiveConfig}
            dateField={effectiveConfig.calendar?.dateField ?? 'dueDate'}
            selectedNumber={search.card}
            onOpen={(task) => openCard(task.number)}
            onReschedule={reschedule}
          />
        )}
      </div>

      {/* §13.3 — side peek, ~640px; a full-screen sheet on mobile (§13.6). */}
      {/* #44 — `peek` holds the card number through the closing slide. */}
      {peek.number !== undefined ? (
        <CardAside open={peek.open}>
          <PeekPanel
            number={peek.number}
            focusDescription={search.focus === 'description'}
            onClose={() => openCard(undefined)}
            neighbours={neighbours}
            onNavigate={openCard}
          />
        </CardAside>
      ) : null}

      <BulkToolbar
        selected={selected}
        lookups={lookups}
        people={people}
        isAdmin={me.role === 'admin'}
        project={{ id: project.id, sections: project.sections }}
        onRun={runBulk}
        onClear={() => setSelected([])}
      />

      <StatusUpdateDialog
        open={postingStatus}
        onOpenChange={setPostingStatus}
        projectId={project.id}
      />

      {/* #9 */}
      <ProjectAccessDialog
        open={managingAccess}
        onOpenChange={setManagingAccess}
        projectId={project.id}
        projectName={project.name}
      />

      {/* #27 */}
      <ProjectFieldsDialog
        open={configuringFields}
        onOpenChange={setConfiguringFields}
        projectId={project.id}
        projectName={project.name}
        cardFields={project.cardFields}
      />

      {/* §6.1 — "deleting a section moves its cards to '(No section)'". Said
          out loud, because the cards are the part people worry about. */}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={`Delete “${deleting?.name ?? ''}”?`}
        description="The section goes; its cards move to “(No section)” in this project. Nothing is deleted."
        confirmLabel="Delete section"
        destructive
        onConfirm={() => {
          if (deleting?.id) sectionMutations.remove.mutate(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
}

function ProjectHeader({
  project,
  onNewCard,
  onPostStatus,
  onManageAccess,
  onConfigureFields,
}: {
  project: ReturnType<typeof useProject>['data'] & object;
  onNewCard: () => void;
  onPostStatus: () => void;
  onManageAccess: () => void;
  onConfigureFields: () => void;
}): JSX.Element {
  return (
    // MOB-13 — below `md` this header and the view bar were 316px of chrome
    // before a single card. There, the compact row inside the view bar carries
    // the project's identity instead.
    <header className="hidden border-b px-4 py-3 md:block">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
            <span
              className="size-3 shrink-0 rounded"
              style={{ backgroundColor: project.color }}
              aria-hidden="true"
            />
            <span className="truncate">{project.name}</span>
            <span className="text-muted-foreground font-mono text-xs">{project.key}</span>
            {project.status === 'archived' ? (
              <Badge variant="secondary">
                <Archive className="size-3" /> Archived
              </Badge>
            ) : null}
            {/* #9 — a private project says so wherever it is named, so nobody
                pastes a link to it and wonders why the other person 404s. */}
            {project.isPrivate ? (
              <Badge variant="secondary">
                <Lock className="size-3" /> Private
              </Badge>
            ) : null}
          </h1>
          {project.latestStatus ? (
            <p className="text-muted-foreground mt-1 flex flex-wrap items-center gap-1.5 text-xs">
              <Badge variant={HEALTH[project.latestStatus.health].variant}>
                {HEALTH[project.latestStatus.health].emoji}{' '}
                {HEALTH[project.latestStatus.health].label}
              </Badge>
              <span className="truncate">{project.latestStatus.note}</span>
              <span>· {relativeTime(project.latestStatus.createdAt)}</span>
              {project.latestStatus.isStale ? (
                <span className="text-amber-600 dark:text-amber-400">· stale</span>
              ) : null}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <FollowButton
            projectId={project.id}
            following={project.isFollowing}
            count={project.followerCount}
          />
          <Button variant="ghost" size="sm" onClick={onManageAccess}>
            {project.isPrivate ? <Lock className="size-4" /> : <LockOpen className="size-4" />}
            <span className="hidden sm:inline">Access</span>
          </Button>
          {/* #27 */}
          <ProjectFieldsButton onClick={onConfigureFields} />
          <Button variant="ghost" size="sm" onClick={onPostStatus}>
            <MessageSquarePlus className="size-4" />
            <span className="hidden sm:inline">Post status</span>
          </Button>
          <Button size="sm" onClick={onNewCard}>
            <Plus className="size-4" />
            New card
          </Button>
        </div>
      </div>
    </header>
  );
}

/**
 * The phone header: who you are looking at, and the one action you came for.
 * Everything else about the view is one tap away behind the view bar's
 * "View" button (MOB-13).
 */
function CompactProjectHeader({
  project,
  onNewCard,
}: {
  project: ReturnType<typeof useProject>['data'] & object;
  onNewCard: () => void;
}): JSX.Element {
  return (
    <>
      <span
        className="size-3 shrink-0 rounded"
        style={{ backgroundColor: project.color }}
        aria-hidden="true"
      />
      <h1 className="min-w-0 flex-1 truncate text-base font-semibold tracking-tight">
        {project.name}
      </h1>
      <Button size="sm" className="h-9 shrink-0" onClick={onNewCard}>
        <Plus className="size-4" />
        <span className="sr-only sm:not-sr-only">New card</span>
      </Button>
    </>
  );
}

const parseViewType = (raw: string): ViewType | undefined =>
  (VIEW_TYPES as readonly string[]).includes(raw) ? (raw as ViewType) : undefined;

function PeekPanel({
  number,
  focusDescription,
  onClose,
  neighbours,
  onNavigate,
}: {
  number: number;
  focusDescription: boolean;
  onClose: () => void;
  /** The cards either side of this one in the list behind the peek. */
  neighbours: { prev: number | null; next: number | null };
  onNavigate: (number: number) => void;
}): JSX.Element {
  const { data, isLoading } = useTaskByNumber(number);
  if (isLoading || !data) return <LoadingRegion label={`Loading #${number}`} />;
  return (
    <CardPanel
      taskId={data.id}
      focusDescription={focusDescription}
      onClose={onClose}
      neighbours={neighbours}
      onNavigate={onNavigate}
    />
  );
}

function FollowButton({
  projectId,
  following,
  count,
}: {
  projectId: string;
  following: boolean;
  count: number;
}): JSX.Element {
  const follow = useFollowProject();
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-pressed={following}
      title={`${count} ${count === 1 ? 'follower' : 'followers'}`}
      onClick={() => follow.mutate({ id: projectId, following: !following })}
    >
      <Star className={cn('size-4', following && 'fill-current text-amber-500')} />
      <span className="hidden sm:inline">{following ? 'Following' : 'Follow'}</span>
    </Button>
  );
}

/**
 * §6.17's disclosure, stated the first time a person invokes an AI action.
 *
 * A confirm rather than a dialog here because the status-update modal is already
 * open: stacking a second dialog on it to say one sentence is worse than the
 * browser's own prompt, and the sentence is the point.
 */
const AI_DISCLOSURE =
  'Drafting sends this project’s last 14 days of activity to Anthropic and fills in the form below. Nothing is posted until you press Post. It happens only when you click — never in the background.';

/** §6.14 — health + note, posted from the project header, notifying followers. */
function StatusUpdateDialog({
  open,
  onOpenChange,
  projectId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
}): JSX.Element {
  const post = usePostStatusUpdate(projectId);
  const history = useStatusUpdates(open ? projectId : null);
  const [health, setHealth] = useState<ProjectHealth>('on_track');
  const [note, setNote] = useState('');
  // §6.17 — "the lead edits and posts". The draft fills this form; nothing is
  // posted until the button below is pressed.
  const ai = useAiStatus();
  const draft = useDraftStatus();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Post a status update</DialogTitle>
          <DialogDescription>
            Dashboards show the numbers; this carries the narrative. Followers are notified.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            {(Object.keys(HEALTH) as ProjectHealth[]).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={health === value}
                onClick={() => setHealth(value)}
                className={cn(
                  'flex-1 rounded-md border px-3 py-2 text-sm transition-colors',
                  health === value ? 'border-primary bg-accent' : 'hover:bg-accent/50',
                )}
              >
                {HEALTH[value].emoji} {HEALTH[value].label}
              </button>
            ))}
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="status-note">What is going on</Label>
              {ai.data?.enabled ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={draft.isPending}
                  onClick={() => {
                    if (!hasSeenDisclosure() && !window.confirm(AI_DISCLOSURE)) return;
                    recordDisclosure();
                    draft.mutate(projectId, {
                      onSuccess: (result) => {
                        setHealth(result.health);
                        setNote(result.note);
                      },
                    });
                  }}
                >
                  <Sparkles />
                  {draft.isPending ? 'Reading the last 14 days…' : 'Draft with AI'}
                </Button>
              ) : null}
            </div>
            <textarea
              id="status-note"
              rows={4}
              className="border-input bg-background w-full rounded-md border p-2 text-sm"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="We are slipping because the payment provider moved their date."
            />
          </div>

          {(history.data?.length ?? 0) > 0 ? (
            <div className="max-h-40 space-y-2 overflow-y-auto border-t pt-2">
              <p className="text-muted-foreground text-xs font-medium">Previous updates</p>
              {history.data?.map((update) => (
                <div key={update.id} className="text-xs">
                  <span>{HEALTH[update.health].emoji}</span>{' '}
                  <span className="font-medium">{update.createdBy.name}</span>{' '}
                  <span className="text-muted-foreground">{relativeTime(update.createdAt)}</span>
                  <p className="text-muted-foreground">{update.note}</p>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={note.trim() === '' || post.isPending}
            onClick={() =>
              post.mutate(
                { health, note: note.trim() },
                {
                  onSuccess: () => {
                    setNote('');
                    onOpenChange(false);
                  },
                },
              )
            }
          >
            Post update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
